/**
 * Weapon System
 *
 * Handles weapon firing based on input (spacebar) and cooldown management.
 * Creates projectile entities when fire conditions are met:
 * - Spacebar action is active (from InputSystem)
 * - Weapon cooldown has expired
 *
 * Supports multiple weapon types:
 * - Single Shot: Default, single projectile
 * - Spread Shot: 3 projectiles in 15-degree spread
 * - Laser: Continuous beam with energy system
 * - Homing: (placeholder for Task 5.7)
 *
 * Weapon switching via:
 * - Number keys 1, 2, 3 for direct selection
 * - Z/X keys for cycling through weapons
 *
 * Emits 'weaponFired', 'weaponChanged', and 'laserFired' events.
 *
 * Following ADR-0001: ECS architecture for game entity management.
 */

import { Vector3 } from 'three'
import { Asteroid, Projectile, Transform, Weapon } from '../components'
import { gameConfig } from '../config'
import { WEAPON_CONFIGS, getNextWeapon, getPreviousWeapon } from '../config/weaponConfig'
import { componentClass } from '../ecs/types'
import type { EntityId, World as IWorld, System } from '../ecs/types'
import { createProjectile } from '../entities/createProjectile'
import type { WeaponType } from '../types/components'
import type { InputSystem } from './InputSystem'
import type { PowerUpSystem } from './PowerUpSystem'

// Type assertions for component classes to work with ECS type system
const TransformClass = componentClass(Transform)
const WeaponClass = componentClass(Weapon)
const AsteroidClass = componentClass(Asteroid)
const ProjectileClass = componentClass(Projectile)

/**
 * Event emitted when a weapon fires.
 * Used by AudioSystem and ParticleSystem for feedback.
 */
export interface WeaponFiredEvent {
  type: 'weaponFired'
  weaponType: string
  position: Vector3
  direction: Vector3
}

/**
 * Event emitted when weapon is changed.
 */
export interface WeaponChangedEvent {
  type: 'weaponChanged'
  entityId: EntityId
  previousWeapon: WeaponType
  newWeapon: WeaponType
}

/**
 * Event emitted when laser fires (continuous).
 */
export interface LaserFiredEvent {
  type: 'laserFired'
  entityId: EntityId
  position: Vector3
  direction: Vector3
  damage: number
}

/**
 * WeaponSystem - handles weapon firing logic for entities with Weapon components.
 *
 * Queries for entities with Weapon and Transform components, checks for fire input,
 * and creates projectiles when cooldown allows.
 */
export class WeaponSystem implements System {
  /** Input system reference for reading fire action */
  private inputSystem: InputSystem

  /** Power-up system reference for checking active effects (optional) */
  private powerUpSystem: PowerUpSystem | null

  /** Cumulative time in milliseconds for cooldown tracking */
  private currentTime = 0

  /** Events emitted this frame (cleared each update) */
  private events: WeaponFiredEvent[] = []

  /** Weapon changed events emitted this frame */
  private weaponChangedEvents: WeaponChangedEvent[] = []

  /** Laser fired events emitted this frame */
  private laserFiredEvents: LaserFiredEvent[] = []

  /** Track which weapon switch actions were processed (to prevent repeat) */
  private processedSwitchActions: Set<string> = new Set()

  /**
   * Create a WeaponSystem instance.
   *
   * @param inputSystem - InputSystem for reading fire action state
   * @param powerUpSystem - Optional PowerUpSystem for checking active effects
   */
  constructor(inputSystem: InputSystem, powerUpSystem?: PowerUpSystem) {
    this.inputSystem = inputSystem
    this.powerUpSystem = powerUpSystem ?? null
  }

  /**
   * Update weapon system - check for fire input and create projectiles.
   *
   * @param world - The ECS world instance
   * @param deltaTime - Time elapsed since last frame in milliseconds
   */
  update(world: IWorld, deltaTime: number): void {
    // Update cumulative time for cooldown tracking
    this.currentTime += deltaTime

    // Clear events from previous frame
    this.events = []
    this.weaponChangedEvents = []
    this.laserFiredEvents = []

    // Query for entities with Weapon and Transform components
    const entities = world.query(TransformClass, WeaponClass)

    for (const entityId of entities) {
      const transform = world.getComponent(entityId, TransformClass)
      const weapon = world.getComponent(entityId, WeaponClass)

      // Safety check - components should exist from query
      if (!transform || !weapon) {
        continue
      }

      // Process weapon switching first
      this.processWeaponSwitching(entityId, weapon)

      // Handle energy regeneration for laser (when not firing)
      const shouldFire = this.inputSystem.hasAction('shoot')
      const isLaserWeapon = weapon.currentWeapon === 'laser'

      if (isLaserWeapon) {
        if (shouldFire) {
          // Try to fire laser
          this.handleLaserFiring(world, entityId, transform, weapon)
        } else {
          // Regenerate energy when not firing
          this.regenerateEnergy(weapon)
        }
      } else {
        // Handle non-laser weapons
        if (shouldFire) {
          this.handleStandardFiring(world, entityId, transform, weapon)
        }
      }
    }

    // Clear processed switch actions at end of frame
    this.updateProcessedSwitchActions()
  }

  /**
   * Process weapon switching based on input.
   *
   * @param entityId - Entity ID
   * @param weapon - Weapon component
   */
  private processWeaponSwitching(entityId: EntityId, weapon: Weapon): void {
    const previousWeapon = weapon.currentWeapon
    let newWeapon: WeaponType | null = null

    // Direct selection via number keys
    if (
      this.inputSystem.hasAction('switchWeapon1') &&
      !this.processedSwitchActions.has('switchWeapon1')
    ) {
      newWeapon = 'single'
      this.processedSwitchActions.add('switchWeapon1')
    } else if (
      this.inputSystem.hasAction('switchWeapon2') &&
      !this.processedSwitchActions.has('switchWeapon2')
    ) {
      newWeapon = 'spread'
      this.processedSwitchActions.add('switchWeapon2')
    } else if (
      this.inputSystem.hasAction('switchWeapon3') &&
      !this.processedSwitchActions.has('switchWeapon3')
    ) {
      newWeapon = 'laser'
      this.processedSwitchActions.add('switchWeapon3')
    } else if (
      this.inputSystem.hasAction('switchWeapon4') &&
      !this.processedSwitchActions.has('switchWeapon4')
    ) {
      newWeapon = 'homing'
      this.processedSwitchActions.add('switchWeapon4')
    }

    // Cycling via Z/X keys
    if (
      this.inputSystem.hasAction('switchWeaponPrev') &&
      !this.processedSwitchActions.has('switchWeaponPrev')
    ) {
      newWeapon = getPreviousWeapon(weapon.currentWeapon)
      this.processedSwitchActions.add('switchWeaponPrev')
    } else if (
      this.inputSystem.hasAction('switchWeaponNext') &&
      !this.processedSwitchActions.has('switchWeaponNext')
    ) {
      newWeapon = getNextWeapon(weapon.currentWeapon)
      this.processedSwitchActions.add('switchWeaponNext')
    }

    // Apply weapon change if different from current
    if (newWeapon !== null && newWeapon !== previousWeapon) {
      weapon.currentWeapon = newWeapon

      // Update cooldown based on new weapon config
      const weaponConfig = WEAPON_CONFIGS[newWeapon]
      weapon.cooldown = weaponConfig.cooldown

      // Emit weapon changed event
      this.weaponChangedEvents.push({
        type: 'weaponChanged',
        entityId,
        previousWeapon,
        newWeapon
      })
    }
  }

  /**
   * Update processed switch actions - remove actions that are no longer active.
   */
  private updateProcessedSwitchActions(): void {
    const actionsToCheck: readonly [
      'switchWeapon1',
      'switchWeapon2',
      'switchWeapon3',
      'switchWeapon4',
      'switchWeaponPrev',
      'switchWeaponNext'
    ] = [
      'switchWeapon1',
      'switchWeapon2',
      'switchWeapon3',
      'switchWeapon4',
      'switchWeaponPrev',
      'switchWeaponNext'
    ]
    for (const action of actionsToCheck) {
      if (!this.inputSystem.hasAction(action)) {
        this.processedSwitchActions.delete(action)
      }
    }
  }

  /**
   * Handle standard (non-laser) weapon firing.
   *
   * @param world - The ECS world
   * @param entityId - Entity ID
   * @param transform - Transform component
   * @param weapon - Weapon component
   */
  private handleStandardFiring(
    world: IWorld,
    entityId: EntityId,
    transform: Transform,
    weapon: Weapon
  ): void {
    // Check if weapon can fire (cooldown expired)
    if (!weapon.canFire(this.currentTime)) {
      return
    }

    // For homing missiles, check ammo
    if (weapon.currentWeapon === 'homing') {
      if (!weapon.hasAmmo()) {
        return // No ammo, cannot fire
      }
    }

    // Check for multiShot powerup effect
    const hasMultiShot = this.powerUpSystem?.hasActiveEffect(entityId, 'multiShot') ?? false

    // Determine how many projectiles will be fired
    const willFireSpread = hasMultiShot || weapon.currentWeapon === 'spread'
    const projectilesToFire = willFireSpread ? 3 : 1

    // Check max player projectiles limit (classic Asteroids: 4)
    const currentProjectiles = this.countPlayerProjectiles(world, entityId)
    const maxProjectiles = gameConfig.gameplay.maxPlayerProjectiles

    if (currentProjectiles + projectilesToFire > maxProjectiles) {
      return // Cannot fire, would exceed max projectiles
    }

    // Fire based on weapon type and multiShot effect
    if (hasMultiShot || weapon.currentWeapon === 'spread') {
      this.fireSpreadShot(world, entityId, transform, weapon)
    } else if (weapon.currentWeapon === 'homing') {
      this.fireHomingMissile(world, entityId, transform, weapon)
    } else {
      this.fireSingleShot(world, entityId, transform, weapon)
    }

    // Record fire time for cooldown
    weapon.recordFire(this.currentTime)

    // Emit event for audio/visual feedback
    this.emitWeaponFiredEvent(weapon, transform)
  }

  /**
   * Count the number of projectiles currently owned by an entity.
   *
   * @param world - The ECS world
   * @param ownerId - EntityId of the owner to count projectiles for
   * @returns Number of projectiles owned by the entity
   */
  private countPlayerProjectiles(world: IWorld, ownerId: EntityId): number {
    const projectiles = world.query(ProjectileClass)
    let count = 0

    for (const projectileId of projectiles) {
      const projectile = world.getComponent(projectileId, ProjectileClass)
      if (projectile && projectile.owner === ownerId) {
        count++
      }
    }

    return count
  }

  /**
   * Handle laser weapon firing (continuous beam with energy).
   *
   * @param world - The ECS world
   * @param entityId - Entity ID
   * @param transform - Transform component
   * @param weapon - Weapon component
   */
  private handleLaserFiring(
    _world: IWorld,
    entityId: EntityId,
    transform: Transform,
    weapon: Weapon
  ): void {
    const laserConfig = WEAPON_CONFIGS.laser
    const energyCost = laserConfig.energyCost ?? 10

    // Check if enough energy to fire
    if (weapon.energy < energyCost) {
      // Not enough energy, regenerate instead
      this.regenerateEnergy(weapon)
      return
    }

    // Drain energy
    weapon.energy -= energyCost

    // Get direction
    const direction = this.getShipForwardDirection(transform.rotation.z)

    // Emit laser fired event
    this.laserFiredEvents.push({
      type: 'laserFired',
      entityId,
      position: transform.position.clone(),
      direction: direction.clone(),
      damage: laserConfig.damage
    })
  }

  /**
   * Regenerate energy for laser weapon.
   *
   * @param weapon - Weapon component
   */
  private regenerateEnergy(weapon: Weapon): void {
    const laserConfig = WEAPON_CONFIGS.laser
    const energyRegen = laserConfig.energyRegen ?? 5
    const maxEnergy = laserConfig.maxEnergy ?? 100

    weapon.energy = Math.min(weapon.energy + energyRegen, maxEnergy)
  }

  /**
   * Fire a single shot projectile.
   *
   * @param world - The ECS world
   * @param shipId - Entity ID of the ship
   * @param transform - Transform component
   * @param weapon - Weapon component
   */
  private fireSingleShot(
    world: IWorld,
    shipId: EntityId,
    transform: Transform,
    weapon: Weapon
  ): void {
    const weaponConfig = WEAPON_CONFIGS[weapon.currentWeapon]
    const direction = this.getShipForwardDirection(transform.rotation.z)

    // Calculate projectile spawn position (slightly ahead of ship)
    const spawnDistance = 21 // Ship radius (20) + offset (1)
    const spawnOffset = direction.clone().multiplyScalar(spawnDistance)
    const projectilePosition = transform.position.clone().add(spawnOffset)

    // Create projectile entity
    createProjectile(world, {
      position: projectilePosition,
      direction: direction.clone(),
      type: weapon.currentWeapon,
      owner: shipId,
      damage: weaponConfig.damage,
      speed: weaponConfig.projectileSpeed
    })
  }

  /**
   * Fire spread shot (3 projectiles in 15-degree spread).
   *
   * @param world - The ECS world
   * @param shipId - Entity ID of the ship
   * @param transform - Transform component
   * @param weapon - Weapon component
   */
  private fireSpreadShot(
    world: IWorld,
    shipId: EntityId,
    transform: Transform,
    _weapon: Weapon
  ): void {
    const weaponConfig = WEAPON_CONFIGS.spread
    const spreadAngle = (weaponConfig.spreadAngle ?? 15) * (Math.PI / 180) // Convert to radians
    const projectileCount = weaponConfig.projectileCount ?? 3

    const baseRotation = transform.rotation.z
    const spawnDistance = 21 // Ship radius (20) + offset (1)

    // Fire projectiles at different angles: center, -15deg, +15deg
    const angles = [0, -spreadAngle, spreadAngle]

    for (let i = 0; i < projectileCount; i++) {
      const angleOffset = angles[i] ?? 0
      const projectileRotation = baseRotation + angleOffset
      const direction = this.getShipForwardDirection(projectileRotation)

      // Calculate spawn position in the direction of this projectile
      const spawnOffset = direction.clone().multiplyScalar(spawnDistance)
      const projectilePosition = transform.position.clone().add(spawnOffset)

      createProjectile(world, {
        position: projectilePosition,
        direction: direction.clone(),
        type: 'spread',
        owner: shipId,
        damage: weaponConfig.damage,
        speed: weaponConfig.projectileSpeed
      })
    }
  }

  /**
   * Fire a homing missile projectile.
   *
   * @param world - The ECS world
   * @param shipId - Entity ID of the ship
   * @param transform - Transform component
   * @param weapon - Weapon component
   */
  private fireHomingMissile(
    world: IWorld,
    shipId: EntityId,
    transform: Transform,
    weapon: Weapon
  ): void {
    const weaponConfig = WEAPON_CONFIGS.homing
    const direction = this.getShipForwardDirection(transform.rotation.z)

    // Calculate projectile spawn position (slightly ahead of ship)
    const spawnDistance = 21 // Ship radius (20) + offset (1)
    const spawnOffset = direction.clone().multiplyScalar(spawnDistance)
    const projectilePosition = transform.position.clone().add(spawnOffset)

    // Find nearest target within homing range
    const homingRange = weaponConfig.homingRange ?? 500
    const homingTarget = this.findNearestTarget(world, transform.position, homingRange)

    // Create projectile entity with homing target
    createProjectile(world, {
      position: projectilePosition,
      direction: direction.clone(),
      type: 'homing',
      owner: shipId,
      damage: weaponConfig.damage,
      speed: weaponConfig.projectileSpeed,
      homingTarget: homingTarget ?? undefined
    })

    // Consume ammo
    weapon.consumeAmmo(1)
  }

  /**
   * Find the nearest asteroid target within range.
   *
   * @param world - The ECS world
   * @param position - Position to search from
   * @param range - Maximum range to acquire target
   * @returns EntityId of nearest asteroid, or null if none in range
   */
  private findNearestTarget(world: IWorld, position: Vector3, range: number): EntityId | null {
    // Query for all asteroids
    const asteroids = world.query(TransformClass, AsteroidClass)

    let nearestTarget: EntityId | null = null
    let nearestDistance = range

    for (const asteroidId of asteroids) {
      const asteroidTransform = world.getComponent(asteroidId, TransformClass)
      if (!asteroidTransform) continue

      const distance = position.distanceTo(asteroidTransform.position)
      if (distance < nearestDistance) {
        nearestDistance = distance
        nearestTarget = asteroidId
      }
    }

    return nearestTarget
  }

  /**
   * Get the ship's forward direction from Z rotation.
   *
   * Ship rotation convention (positive rotation = counter-clockwise):
   * - rotation.z = 0 -> facing +Y (up)
   * - rotation.z = PI/2 -> facing -X (left, after turning left 90°)
   * - rotation.z = PI -> facing -Y (down)
   * - rotation.z = -PI/2 -> facing +X (right, after turning right 90°)
   *
   * This matches the ShipControlSystem rotation convention.
   *
   * @param rotationZ - Z rotation in radians
   * @returns Normalized direction vector
   */
  private getShipForwardDirection(rotationZ: number): Vector3 {
    // Convert rotation to direction using sine/cosine
    // At rotation=0, ship faces +Y (up)
    // Negative sin ensures CCW rotation gives correct direction
    const x = -Math.sin(rotationZ)
    const y = Math.cos(rotationZ)
    return new Vector3(x, y, 0).normalize()
  }

  /**
   * Emit a weaponFired event for audio/visual feedback.
   *
   * @param weapon - Weapon component
   * @param transform - Transform component
   */
  private emitWeaponFiredEvent(weapon: Weapon, transform: Transform): void {
    const event: WeaponFiredEvent = {
      type: 'weaponFired',
      weaponType: weapon.currentWeapon,
      position: transform.position.clone(),
      direction: this.getShipForwardDirection(transform.rotation.z)
    }
    this.events.push(event)
  }

  /**
   * Get events emitted during the last update.
   * Events are cleared at the start of each update.
   *
   * @returns Array of weapon fired events
   */
  getEvents(): WeaponFiredEvent[] {
    return this.events
  }

  /**
   * Get weapon changed events emitted during the last update.
   *
   * @returns Array of weapon changed events
   */
  getWeaponChangedEvents(): WeaponChangedEvent[] {
    return this.weaponChangedEvents
  }

  /**
   * Get laser fired events emitted during the last update.
   *
   * @returns Array of laser fired events
   */
  getLaserFiredEvents(): LaserFiredEvent[] {
    return this.laserFiredEvents
  }
}
